import { Injectable, OnDestroy, inject } from '@angular/core';
import {Location} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import * as routes from '@app/routes';
import {getLoggedInUser} from '@app/stores/authentication/authentication.selectors';
import {getProject} from '@app/stores/projects/projects.selectors';
import {ProjectGet} from '@app/stores/projects/projects.actions';

export interface ProjectContext {
  projectId: number | null;
  /** On one project's pages: /projects/:id/... or /project/:id/..., but not the list or "create". */
  inProjectContext: boolean;
  /** That project, once loaded - never another page's project left in the store. */
  project: any | null;
}

/**
 * Which project, if any, the page on screen belongs to - read from the URL on every
 * navigation. The menus resolve `:projectId` from it, the switcher and breadcrumb name it.
 */
@Injectable({providedIn: 'root'})
export class ProjectContextService implements OnDestroy {
  private router = inject(Router);
  private location = inject(Location);
  private store = inject<Store<any>>(Store);

  private readonly contextSubject = new BehaviorSubject<ProjectContext>({projectId: null, inProjectContext: false, project: null});
  private readonly destroyed = new Subject<void>();
  private signedIn = false;

  readonly context$: Observable<ProjectContext> = this.contextSubject.pipe(
    distinctUntilChanged((a, b) => a.projectId === b.projectId && a.inProjectContext === b.inProjectContext
      && a.project === b.project)
  );

  constructor() {
    this.store.pipe(select(getLoggedInUser), takeUntil(this.destroyed)).subscribe((user) => {
      this.signedIn = !!user;
      this.readUrl();
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.destroyed))
      .subscribe(() => this.readUrl());
    this.store.pipe(select(getProject), takeUntil(this.destroyed)).subscribe((project) => {
      const context = this.contextSubject.value;
      if (project && context.projectId && Number(project.id) === context.projectId && project !== context.project) {
        this.contextSubject.next({...context, project});
      }
    });
    this.readUrl();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get context(): ProjectContext {
    return this.contextSubject.value;
  }

  /** A project's front page: its campaigns, or its share page for an influencer. */
  projectHome(projectId: number, isInfluencer: boolean): string {
    return '/' + (isInfluencer ? routes.shareProject(projectId) : routes.campaigns({projectId}));
  }

  /**
   * Open another project, staying on the same kind of page where that makes sense - its
   * campaigns rather than a campaign that belongs to the project being left.
   */
  switchProject(projectId: number, isInfluencer: boolean): void {
    if (isInfluencer) {
      this.router.navigate([routes.shareProject(projectId)]);
      return;
    }
    const urlParts = this.router.url.split('/').filter((part) => part);
    if (Number(urlParts[1]) === Number(projectId) && this.context.inProjectContext) {
      return;
    }
    if (!this.context.inProjectContext) {
      this.router.navigateByUrl(routes.campaigns({projectId}));
      return;
    }
    urlParts[1] = String(projectId);
    let finalUrl = '/' + urlParts.join('/');
    if (['campaigns', 'add-influencer', 'influencer/'].some((part) => finalUrl.includes(part))) {
      this.router.navigateByUrl(routes.campaigns({projectId}));
    } else if (finalUrl.includes('influencers/view')) {
      this.router.navigateByUrl(routes.influencers({projectId}));
    } else if (finalUrl.includes('registration-platforms')) {
      this.router.navigateByUrl(routes.thirdpartyPlatforms({projectId}));
    } else {
      if (finalUrl.includes('statistics')) {
        finalUrl = finalUrl.split('?')[0];
      }
      this.router.navigateByUrl(finalUrl);
    }
  }

  /** The current project's settings, coming back here afterwards. */
  editProject(): void {
    const project = this.context.project;
    if (!project) {
      return;
    }
    const url = '/' + routes.editProject(project.id);
    if (this.router.url.split('?')[0] === url) {
      return;
    }
    this.router.navigateByUrl('/' + routes.editProject(project.id, {returnUrl: this.router.url}));
  }

  private readUrl(): void {
    const path = this.location.path().replace(/\?.*$/, '');
    const match = path.match(/^\/projects?\/(\d+)/);
    const projectId = match ? Number(match[1]) : null;
    const inProjectContext = this.signedIn && !!projectId
      && path !== `/${routes.projects()}` && path !== `/${routes.createProject()}`;
    const previous = this.contextSubject.value;
    const project = previous.project && Number(previous.project.id) === projectId ? previous.project : null;
    if (previous.projectId === projectId && previous.inProjectContext === inProjectContext && previous.project === project) {
      return;
    }
    this.contextSubject.next({projectId, inProjectContext, project});
    if (this.signedIn && projectId && !project) {
      this.store.dispatch(ProjectGet({projectId}));
    }
  }
}
